import React, { useState, useEffect, useCallback } from 'react';
import { dokumenteV2Api } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #334155' };
const btn = (color = '#6366f1') => ({
  background: color, color: '#fff', border: 'none', borderRadius: 6,
  padding: '6px 12px', cursor: 'pointer', fontSize: 12,
});

const RISIKO_COLOR = { hoch: '#ef4444', mittel: '#f59e0b', niedrig: '#22c55e' };
const STATUS_BADGE = {
  offen: { bg: '#1e293b', color: '#94a3b8', label: 'Offen' },
  bestaetigt: { bg: '#064e3b', color: '#22c55e', label: '✅ Bestätigt' },
  abgelehnt: { bg: '#450a0a', color: '#ef4444', label: '❌ Abgelehnt' },
  ausstehend: { bg: '#1e293b', color: '#94a3b8', label: 'Ausstehend' },
};

export default function PruefCenterPage() {
  const [tab, setTab] = useState('felder');
  const [dokumente, setDokumente] = useState([]);
  const [selectedDok, setSelectedDok] = useState(null);
  const [felder, setFelder] = useState([]);
  const [klauseln, setKlauseln] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: 'ausstehend', risiko: '' });
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    loadDokumente();
  }, []);

  async function loadDokumente() {
    try {
      const r = await dokumenteV2Api.list({ status: 'abgeschlossen', limit: 30 });
      const docs = r.data?.dokumente || [];
      setDokumente(docs);
      if (docs.length > 0 && !selectedDok) {
        setSelectedDok(docs[0]);
        loadDokumentFelder(docs[0].id);
        loadDokumentKlauseln(docs[0].id);
      }
    } catch {}
  }

  async function loadDokumentFelder(dokId) {
    setLoading(true);
    try {
      const r = await dokumenteV2Api.felder(dokId);
      const all = Array.isArray(r.data) ? r.data : [];
      setFelder(all);
    } catch {}
    setLoading(false);
  }

  async function loadDokumentKlauseln(dokId) {
    try {
      const r = await dokumenteV2Api.klauseln(dokId);
      setKlauseln(Array.isArray(r.data) ? r.data : []);
    } catch {}
  }

  function selectDok(dok) {
    setSelectedDok(dok);
    loadDokumentFelder(dok.id);
    loadDokumentKlauseln(dok.id);
    setMsg(null);
  }

  async function feldBestaetigen(feldId) {
    if (!selectedDok) return;
    try {
      await dokumenteV2Api.feldBestaetigen(selectedDok.id, feldId);
      setFelder(prev => prev.map(f => f.id === feldId ? { ...f, bestaetigt: 1, status: 'bestaetigt', review_status: 'bestaetigt' } : f));
      setMsg({ type: 'ok', text: 'Feld bestätigt.' });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
  }

  async function feldAblehnen(feldId) {
    if (!selectedDok) return;
    try {
      await dokumenteV2Api.feldAblehnen(selectedDok.id, feldId, 'Manuell abgelehnt');
      setFelder(prev => prev.map(f => f.id === feldId ? { ...f, abgelehnt: 1, status: 'abgelehnt', review_status: 'abgelehnt' } : f));
      setMsg({ type: 'ok', text: 'Feld abgelehnt.' });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
  }

  async function feldUebernehmen(feldId) {
    if (!selectedDok) return;
    try {
      const r = await dokumenteV2Api.feldUebernehmen(selectedDok.id, feldId);
      setFelder(prev => prev.map(f => f.id === feldId ? { ...f, uebernommen: 1 } : f));
      setMsg({ type: 'ok', text: `Feld übernommen: ${r.data.feldname} = ${r.data.wert}` });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
  }

  async function klauselBestaetigen(klauselId) {
    try {
      await dokumenteV2Api.klauselBestaetigen(klauselId);
      setKlauseln(prev => prev.map(k => k.id === klauselId ? { ...k, bestaetigt: 1, status: 'bestaetigt' } : k));
      setMsg({ type: 'ok', text: 'Klausel bestätigt.' });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
  }

  async function klauselAblehnen(klauselId) {
    try {
      await dokumenteV2Api.klauselAblehnen(klauselId, 'Manuell abgelehnt');
      setKlauseln(prev => prev.map(k => k.id === klauselId ? { ...k, abgelehnt: 1, status: 'abgelehnt' } : k));
      setMsg({ type: 'ok', text: 'Klausel abgelehnt.' });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
  }

  async function analysiereKlauseln() {
    if (!selectedDok) return;
    setLoading(true);
    try {
      await dokumenteV2Api.klauselAnalysieren(selectedDok.id);
      await loadDokumentKlauseln(selectedDok.id);
      setMsg({ type: 'ok', text: 'Klauselanalyse abgeschlossen.' });
    } catch (e) { setMsg({ type: 'err', text: e.response?.data?.error || e.message }); }
    setLoading(false);
  }

  const tabStyle = (t) => ({
    padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400,
    background: tab === t ? '#818cf8' : '#334155', color: tab === t ? '#fff' : '#94a3b8', border: 'none',
  });

  const visibleFelder = felder.filter(f => {
    if (filter.status === 'ausstehend') return !f.bestaetigt && !f.abgelehnt;
    if (filter.status === 'bestaetigt') return f.bestaetigt;
    if (filter.status === 'abgelehnt') return f.abgelehnt;
    return true;
  });

  const visibleKlauseln = klauseln.filter(k => {
    if (filter.risiko) return k.risiko_stufe === filter.risiko;
    return true;
  });

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', color: '#f1f5f9', display: 'flex', gap: 20 }}>
      {/* Sidebar: Dokumente */}
      <div style={{ width: 260, flexShrink: 0 }}>
        <h3 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>DOKUMENTE ({dokumente.length})</h3>
        <div style={{ overflowY: 'auto', maxHeight: 600 }}>
          {dokumente.map(dok => (
            <div
              key={dok.id}
              onClick={() => selectDok(dok)}
              style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                background: selectedDok?.id === dok.id ? '#312e81' : '#1e293b',
                border: `1px solid ${selectedDok?.id === dok.id ? '#6366f1' : '#334155'}`,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', marginBottom: 2 }}>
                {(dok.originaldateiname || dok.dateiname || '').slice(0, 25)}{dok.originaldateiname?.length > 25 ? '…' : ''}
              </div>
              <div style={{ fontSize: 10, color: '#64748b' }}>
                {dok.klassifikation || 'Unklassifiziert'} · {dok.risiko_stufe ? `⚠️ ${dok.risiko_stufe}` : ''}
              </div>
              {dok.dokumentqualitaet_score !== null && (
                <div style={{ fontSize: 10, color: dok.dokumentqualitaet_score >= 70 ? '#22c55e' : dok.dokumentqualitaet_score >= 40 ? '#f59e0b' : '#ef4444' }}>
                  Qualität: {dok.dokumentqualitaet_score}/100
                </div>
              )}
            </div>
          ))}
          {dokumente.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 12, padding: 8 }}>Keine verarbeiteten Dokumente.</div>
          )}
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            🔍 Prüf-Center{selectedDok ? ` – ${(selectedDok.originaldateiname || '').slice(0, 30)}` : ''}
          </h1>
        </div>

        {msg && (
          <div style={{ background: msg.type === 'ok' ? '#064e3b' : '#450a0a', color: msg.type === 'ok' ? '#22c55e' : '#ef4444',
            padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {msg.text}
            <button onClick={() => setMsg(null)} className="btn-ghost" style={{ marginLeft: 12 }}>✕</button>
          </div>
        )}

        {!selectedDok ? (
          <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
            Dokument aus der Liste auswählen.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button style={tabStyle('felder')} onClick={() => setTab('felder')}>
                🔤 Felder ({felder.length})
              </button>
              <button style={tabStyle('klauseln')} onClick={() => setTab('klauseln')}>
                📋 Klauseln ({klauseln.length})
              </button>
            </div>

            {/* Tab: Felder */}
            {tab === 'felder' && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {['ausstehend', 'bestaetigt', 'abgelehnt', 'alle'].map(s => (
                    <button key={s} onClick={() => setFilter(f => ({ ...f, status: s }))}
                      style={{ ...btn(filter.status === s ? '#6366f1' : '#334155'), fontSize: 11 }}>
                      {s === 'ausstehend' ? '⏳ Offen' : s === 'bestaetigt' ? '✅ Bestätigt' : s === 'abgelehnt' ? '❌ Abgelehnt' : '📋 Alle'}
                      {' '}({felder.filter(f => {
                        if (s === 'ausstehend') return !f.bestaetigt && !f.abgelehnt;
                        if (s === 'bestaetigt') return f.bestaetigt;
                        if (s === 'abgelehnt') return f.abgelehnt;
                        return true;
                      }).length})
                    </button>
                  ))}
                </div>

                {loading ? (
                  <div style={{ color: '#6366f1', textAlign: 'center', padding: 30 }}>Laden…</div>
                ) : visibleFelder.length === 0 ? (
                  <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
                    {filter.status === 'ausstehend' ? 'Alle Felder wurden bereits geprüft.' : 'Keine Felder vorhanden.'}
                  </div>
                ) : visibleFelder.map(f => (
                  <div key={f.id} style={{ ...card, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{f.feldname}</span>
                          {f.konfidenz && (
                            <span style={{ fontSize: 11, color: f.konfidenz >= 0.8 ? '#22c55e' : f.konfidenz >= 0.5 ? '#f59e0b' : '#ef4444' }}>
                              {Math.round(f.konfidenz * 100)}%
                            </span>
                          )}
                          {(STATUS_BADGE[f.review_status || f.status]) && (() => {
                            const badge = STATUS_BADGE[f.review_status || f.status] || STATUS_BADGE.ausstehend;
                            return <span style={{ fontSize: 11, background: badge.bg, color: badge.color, padding: '2px 6px', borderRadius: 10 }}>{badge.label}</span>;
                          })()}
                          {f.uebernommen ? <span style={{ fontSize: 11, color: '#22c55e' }}>📥 Übernommen</span> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                          <div><span style={{ color: '#64748b' }}>Erkannt:</span> <span style={{ color: '#f1f5f9' }}>{f.erkannter_wert || '—'}</span></div>
                          {f.normalisierter_wert !== f.erkannter_wert && (
                            <div><span style={{ color: '#64748b' }}>Normalisiert:</span> <span style={{ color: '#a5f3fc' }}>{f.normalisierter_wert || '—'}</span></div>
                          )}
                        </div>
                        {f.fundstelle && (
                          <div style={{ color: '#64748b', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>{f.fundstelle}</div>
                        )}
                      </div>
                      {!f.bestaetigt && !f.abgelehnt && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => feldBestaetigen(f.id)} style={btn('#22c55e')}>✅</button>
                          <button onClick={() => feldAblehnen(f.id)} className="btn-danger">❌</button>
                        </div>
                      )}
                      {f.bestaetigt && !f.uebernommen && selectedDok?.vertrag_id && (
                        <button onClick={() => feldUebernehmen(f.id)} style={btn('#6366f1')}>📥 Übernehmen</button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Tab: Klauseln */}
            {tab === 'klauseln' && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                  <select value={filter.risiko} onChange={e => setFilter(f => ({ ...f, risiko: e.target.value }))}
                    style={{ background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                    <option value="">Alle Risiken</option>
                    <option value="hoch">⚠️ Hoch</option>
                    <option value="mittel">🟡 Mittel</option>
                    <option value="niedrig">🟢 Niedrig</option>
                  </select>
                  <button onClick={analysiereKlauseln} disabled={loading} style={btn('#8b5cf6')}>
                    {loading ? '⏳' : '🔄'} Neu analysieren
                  </button>
                </div>

                {visibleKlauseln.length === 0 ? (
                  <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
                    <div>Keine Klauseln gefunden.</div>
                    <div style={{ fontSize: 12, marginTop: 8 }}>Klauselanalyse starten, um Klauseln aus dem Dokument zu extrahieren.</div>
                    <button onClick={analysiereKlauseln} style={{ ...btn('#6366f1'), marginTop: 12 }}>Klauselanalyse starten</button>
                  </div>
                ) : visibleKlauseln.map(k => (
                  <div key={k.id} style={{ ...card, borderLeft: `3px solid ${RISIKO_COLOR[k.risiko_stufe] || '#6366f1'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{k.titel || k.klauseltyp}</span>
                          <span style={{ fontSize: 11, color: RISIKO_COLOR[k.risiko_stufe] }}>
                            ⚠️ {k.risiko_stufe}
                          </span>
                          {k.bestaetigt ? <span style={{ fontSize: 11, color: '#22c55e' }}>✅ Bestätigt</span> : null}
                          {k.abgelehnt ? <span style={{ fontSize: 11, color: '#ef4444' }}>❌ Abgelehnt</span> : null}
                          {k.konfidenz ? <span style={{ fontSize: 11, color: '#64748b' }}>{Math.round(k.konfidenz * 100)}%</span> : null}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>
                          {k.inhalt?.slice(0, 300)}{k.inhalt?.length > 300 ? '…' : ''}
                        </div>
                        {k.empfehlung && (
                          <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#818cf8' }}>
                            💡 {k.empfehlung}
                          </div>
                        )}
                      </div>
                      {!k.bestaetigt && !k.abgelehnt && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => klauselBestaetigen(k.id)} style={btn('#22c55e')}>✅</button>
                          <button onClick={() => klauselAblehnen(k.id)} className="btn-danger">❌</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
